import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import Styles from "../styles/UsersList.module.css";
import { userAPI } from "../services/api";
import { UserContext } from "./context/user/UserContext";
import { useDispatch, useSelector } from "react-redux";
import { setUsers, replaceUsers, removeUser } from "../redux/slices/UsersSlice";
import UserContainer from "./UserContainer";
import Loader from "./Loader";
import Toast from "./Toast";
import ConfirmModal from "./ConfirmModal";

const UserList = () => {
  const { userData } = useContext(UserContext);
  const token = userData.token;
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users.users);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const observer = useRef();
  const [toast, setToast] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [usersEnd, setUsersEnd] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, userId: null });

  const lastPostElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && page < totalPages) {
          setPage((prevPageNumber) => prevPageNumber + 1);
        } else {
          setUsersEnd(true);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, page, totalPages]
  );

  const prevSearchRef = useRef(search);
  const getUsers = useCallback(async (searchTerm) => {
    setLoading(true);
    setUsersEnd(false);
    try {
      const response = await userAPI.getAllUsers(token, page, searchTerm);
      setTotalPages(response.totalPages);
      const data = response.data;

      if (prevSearchRef.current !== searchTerm || page === 1) {
        dispatch(replaceUsers(data));
      } else {
        dispatch(setUsers(data));
      }
      prevSearchRef.current = searchTerm;
      if (page === response.totalPages) {
        setUsersEnd(true);
      }
    } catch (error) {
      console.log("Some error occurred:", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, page, token]);
  const handleDeleteClick = (userId) => {
    setConfirmModal({ isOpen: true, userId });
  };

  const handleDeleteConfirm = async () => {
    const userId = confirmModal.userId;
    setToast({ message: 'Deleting user...', type: 'info' });
    
    try {
      await userAPI.deleteUser(token, userId);
      
      // Only remove from list AFTER successful deletion
      dispatch(removeUser(userId));
      
      setToast({ message: 'User deleted successfully! ✅', type: 'success' });
    } catch (error) {
      console.log("Error in deleting:", error);
      setToast({ message: `Failed to delete user: ${error.message}`, type: 'error' });
    }
  };
  const clear = () => {
    setSearch("");
    setUsersEnd(false);
    if (page === 1) {
      getUsers("");
    } else {
      setPage(1);
    }
  };

  useEffect(() => {
    getUsers(search);
  }, [getUsers, search]);

  useEffect(() => {
    dispatch(replaceUsers([]));
    setUsersEnd(false);
    setPage(1);
  }, [dispatch, search]);

  return (
    <div className={Styles.container}>
      <div className={Styles.heading}>
        <h2> USERS</h2>
      </div>
      <div className={Styles.inputContainer}>
        <input
          type="text"
          className={Styles.input}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className={Styles.clear_button} onClick={clear}>
            X
          </button>
        )}
        <button
          className={Styles.search_button}
          onClick={() => getUsers(search)}
        >
          Search
        </button>
      </div>

      <div className={Styles.user_list}>
        <table>
          <thead>
            <tr>
              <th>No</th>
              <th>Username</th>
              <th>User ID</th>
              <th>Email</th>
              <th>Action</th>
            </tr>
          </thead>
          {users?.map((user, index) => {
            if (users.length === index + 1) {
              return (
                <tbody key={index} ref={lastPostElementRef}>
                  <UserContainer
                    user={user}
                    handleDelete={handleDeleteClick}
                    index={index}
                  />
                </tbody>
              );
            } else {
              return (
                <tbody key={index}>
                  <UserContainer
                    user={user}
                    handleDelete={handleDeleteClick}
                    index={index}
                  />
                </tbody>
              );
            }
          })}
        </table>
      </div>
      <div>{usersEnd && !loading && <h3>No More Users</h3>}</div>
      {loading && <Loader />}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, userId: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete User?"
        message="Are you sure you want to delete this user? This action cannot be undone and will permanently remove the user and all their data."
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default UserList;
